import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Field, Empty } from "@/components/app/bits";

export default function AdoptionTab({ changeId }) {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const blank = { name: "", unit: "%", target: 100, current: 0 };
  const [f, setF] = useState(blank);
  const load = () => api.get(`/changes/${changeId}/metrics`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [changeId]); // eslint-disable-line
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async (e) => { e.preventDefault(); try { await api.post(`/changes/${changeId}/metrics`, { ...f, target: +f.target, current: +f.current }); setF(blank); setAdding(false); load(); toast.success("Metric added"); } catch (ex) { toast.error(errMsg(ex)); } };
  const upd = async (m, current) => { await api.put(`/metrics/${m.id}`, { name: m.name, unit: m.unit, target: m.target, current: +current }); load(); };
  const remove = async (id) => { await api.delete(`/metrics/${id}`); load(); };
  if (!items) return null;
  const pcts = items.filter((m) => m.target > 0).map((m) => Math.min(100, (m.current / m.target) * 100));
  const overall = pcts.length ? Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length) : null;
  return (
    <div className="grid lg:grid-cols-[1fr_.6fr] gap-5">
      <div>
        <div className="flex items-center justify-between"><h2 className="font-head text-lg font-extrabold">Success metrics</h2><button data-testid="metric-add-btn" onClick={() => setAdding(!adding)} className="pp-btn-ghost py-1.5"><Plus size={14} /> Add metric</button></div>
        {adding && (
          <form onSubmit={add} className="pp-card p-5 mt-4 grid sm:grid-cols-4 gap-3" data-testid="metric-form">
            <Field label="Metric" className="sm:col-span-2"><input data-testid="mt-name" className="pp-input" required value={f.name} onChange={set("name")} placeholder="e.g. Invoices processed in new ERP" /></Field>
            <Field label="Unit"><input className="pp-input" value={f.unit} onChange={set("unit")} /></Field>
            <Field label="Target"><input data-testid="mt-target" type="number" className="pp-input" value={f.target} onChange={set("target")} /></Field>
            <div className="sm:col-span-4 flex justify-end"><button data-testid="mt-submit" className="pp-btn-dark text-sm py-2">Save</button></div>
          </form>
        )}
        <div className="mt-4 space-y-3">
          {items.length === 0 && <Empty testid="metrics-empty" text="No metrics yet. What will prove the change stuck — usage, proficiency, legacy fall-off?" />}
          {items.map((m) => {
            const pct = m.target > 0 ? Math.min(100, Math.round((m.current / m.target) * 100)) : 0;
            return (
              <div key={m.id} data-testid={`metric-${m.id}`} className="pp-card p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0"><div className="font-bold text-foreground truncate">{m.name}</div><div className="text-xs text-muted-foreground">Target {m.target} {m.unit}</div></div>
                  <div className="flex items-center gap-2"><input data-testid={`metric-current-${m.id}`} type="number" step="any" className="pp-input w-24 text-right" defaultValue={m.current} onBlur={(e) => +e.target.value !== m.current && upd(m, e.target.value)} /><span className="text-xs text-muted-foreground w-10">{m.unit}</span></div>
                  <button aria-label={`Remove metric ${m.name || ""}`} onClick={() => remove(m.id)} className="text-faint hover:text-danger-600 transition-colors"><Trash2 size={14} /></button>
                </div>
                <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-500 ${pct >= 80 ? "bg-brand-500" : pct >= 40 ? "bg-accent-500" : "bg-warning-500"}`} style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="pp-card p-6 text-center h-fit">
        <div className="pp-label">Adoption index</div>
        <div data-testid="adoption-overall" className="font-head text-6xl font-extrabold tracking-tight text-foreground">{overall ?? "—"}<span className="text-2xl text-muted-foreground">%</span></div>
        <p className="text-xs text-muted-foreground mt-3">Average of each metric's progress to target. Update actuals inline — the dashboard rolls them up.</p>
      </div>
    </div>
  );
}
